/**
 * Cross-page SIM selection — ARCHITECTURE §05.5.
 *
 * Two properties this exists to guarantee, both of which are about what the
 * operator can *see*:
 *
 * 1. **Nothing is ever selected that the user has not seen.** "Select all on
 *    this page" selects exactly the rows currently rendered. There is no
 *    "select all matching this filter" and there deliberately never will be: it
 *    cannot honour the cap without either lying about what is selected or
 *    silently truncating, and a filter matching 4,000 SIMs one click from
 *    Disconnect is the hazard the whole phase exists to avoid.
 *
 * 2. **Nothing is ever silently dropped.** At the cap the 101st checkbox does
 *    not tick and says why. The alternative — accepting 120 and sending 100 —
 *    means 20 SIMs the operator believes they acted on are untouched, and they
 *    will not find out until someone complains.
 *
 * The cap message says "you can act on up to 100 at a time", never "the network
 * refuses": `SIM_BATCH_MAX` is **ours** (§03.5), and blaming the upstream for
 * our own bound is how a support conversation goes wrong.
 */

import { SIM_BATCH_MAX, type SimSummary } from '@rd/shared';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

export const CAP_MESSAGE = `You can act on up to ${SIM_BATCH_MAX} SIMs at a time. Clear some, or run this in batches.`;

export interface SelectionApi {
  selected: ReadonlySet<string>;
  count: number;
  cap: number;
  atCap: boolean;
  /** True only if something outside this component put more than the cap in. */
  overCap: boolean;
  /** Set when the user's last action was refused for hitting the cap. */
  capMessage: string | null;
  dismissCapMessage(): void;
  toggle(simRef: string): void;
  /** True when this ref may still be added — drives the disabled checkbox. */
  canSelect(simRef: string): boolean;
  selectPage(refs: string[]): void;
  clear(): void;
  /** The full SimSummary for each selected ref, in selection order. */
  selectedSims(): SimSummary[];
  msisdns(): string[];
  /** Screens call this as rows arrive so the selection can resolve details. */
  remember(sims: SimSummary[]): void;
}

const SelectionContext = createContext<SelectionApi | null>(null);

export function useSelection(): SelectionApi {
  const ctx = useContext(SelectionContext);
  if (!ctx) throw new Error('useSelection must be used inside <SelectionProvider>');
  return ctx;
}

export function SelectionProvider({ children }: { children: ReactNode }): ReactElement {
  // An array, not a Set, because selection ORDER is what the confirmation
  // dialog lists and an operator scanning for the one they did not mean to
  // select is reading that list.
  const [order, setOrder] = useState<string[]>([]);
  const [capMessage, setCapMessage] = useState<string | null>(null);

  // Details for rows the user has seen, so a selection spanning pages can still
  // render every MSISDN in the dialog after the user has paged away.
  const known = useRef(new Map<string, SimSummary>());

  const selected = useMemo(() => new Set(order), [order]);
  const count = order.length;
  const atCap = count >= SIM_BATCH_MAX;

  const remember = useCallback((sims: SimSummary[]) => {
    for (const sim of sims) known.current.set(sim.simRef, sim);
  }, []);

  /**
   * `setOrder`'s updater stays **functional and pure**; only the advisory cap
   * message is set outside it.
   *
   * Two constraints pull against each other here and both matter:
   *
   *   * An updater must be pure — React invokes it twice under StrictMode, and
   *     a `setCapMessage` nested inside it can be discarded. That is what broke
   *     "select all on this page", and a Phase 05 safety spec caught it.
   *   * The updater must still be functional, because several `toggle` calls
   *     can land in one tick; reading `order` from the closure instead makes
   *     each call see the same stale value and only the last survives. A Phase
   *     05 spec caught that too, on the first attempt at this fix.
   *
   * So: state through the updater, message from the current render's `order`.
   * The message is advisory and one render stale at worst, which is invisible.
   */
  const toggle = useCallback(
    (simRef: string) => {
      setOrder((current) => {
        if (current.includes(simRef)) return current.filter((r) => r !== simRef);
        // Refused. No silent truncation, and no request.
        if (current.length >= SIM_BATCH_MAX) return current;
        return [...current, simRef];
      });

      const refused = !order.includes(simRef) && order.length >= SIM_BATCH_MAX;
      setCapMessage(refused ? CAP_MESSAGE : null);
    },
    [order],
  );

  const canSelect = useCallback(
    (simRef: string) => order.includes(simRef) || order.length < SIM_BATCH_MAX,
    [order],
  );

  const selectPage = useCallback(
    (refs: string[]) => {
      setOrder((current) => {
        const next = [...current];
        for (const ref of refs) {
          if (next.includes(ref)) continue;
          if (next.length >= SIM_BATCH_MAX) break;
          next.push(ref);
        }
        return next;
      });

      // Partially filling to the cap is honest — the user sees the count stop
      // and the message explain it — but it must never be silent.
      const room = SIM_BATCH_MAX - order.length;
      const wanted = refs.filter((ref) => !order.includes(ref)).length;
      setCapMessage(wanted > room ? CAP_MESSAGE : null);
    },
    [order],
  );

  const clear = useCallback(() => {
    setOrder([]);
    setCapMessage(null);
  }, []);

  const selectedSims = useCallback(
    () => order.map((ref) => known.current.get(ref)).filter((s): s is SimSummary => Boolean(s)),
    [order],
  );

  const msisdns = useCallback(
    () =>
      order
        .map((ref) => known.current.get(ref)?.msisdn)
        .filter((m): m is string => typeof m === 'string' && m.length > 0),
    [order],
  );

  const value = useMemo<SelectionApi>(
    () => ({
      selected,
      count,
      cap: SIM_BATCH_MAX,
      atCap,
      overCap: count > SIM_BATCH_MAX,
      capMessage,
      dismissCapMessage: () => setCapMessage(null),
      toggle,
      canSelect,
      selectPage,
      clear,
      selectedSims,
      msisdns,
      remember,
    }),
    [
      selected,
      count,
      atCap,
      capMessage,
      toggle,
      canSelect,
      selectPage,
      clear,
      selectedSims,
      msisdns,
      remember,
    ],
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}
